import React from 'react';
import { X, User, Save, AlertTriangle, Check, Building, BookOpen, Hash, Calendar, Loader2 } from 'lucide-react';
import { useUserPreferences, useStoreActions, useDepartments } from '@/store/useStore';
import { useStudentFormLogic, type StudentFormValues } from '@/hooks/useStudentFormLogic';
import { findMatchingBatch, normalizeGroup, normalizeSemester } from '@/utils/programMatching';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ─── Dark design tokens ───────────────────────────────────────────────────────
const T = {
  panel: '#121212',
  surface: 'rgba(255,255,255,.03)',
  border: 'rgba(255,255,255,.07)',
  teal: '#14B8A6',
  tealBg: 'rgba(20,184,166,.1)',
  tealBorder: 'rgba(20,184,166,.22)',
  text: '#fff',
  text60: 'rgba(255,255,255,.6)',
  text40: 'rgba(255,255,255,.4)',
  text25: 'rgba(255,255,255,.25)',
  mono: "'JetBrains Mono',monospace",
  serif: "'Instrument Serif',serif",
  sans: "'Outfit',sans-serif",
};

export function EditProfileModal({ isOpen, onClose }: EditProfileModalProps) {
  const userPreferences = useUserPreferences();
  const { setUserPreferences } = useStoreActions();
  const departments = useDepartments();
  const profileInitialized = React.useRef(false);

  const {
    form,
    handleSubmit,
    selectedDepartment,
    setSelectedDepartment,
    selectedProgram,
    setSelectedProgram,
    setSelectedYear,
    selectedSemester,
    setSelectedSemester,
    submitStatus,
    validationErrors,
    isDataLoading,
    availablePrograms,
    availableYears,
    availableSemesters,
    availableGroups,
    clearFieldError,
    handleFieldChange,
    resetDependentFields,
    getDepartmentForProgram,
  } = useStudentFormLogic({
    initialData: React.useMemo(() => ({
      name: userPreferences?.name || '',
      department: userPreferences?.department || '',
      program: userPreferences?.program || '',
      year: userPreferences?.year?.toString() || '',
      semester: userPreferences?.semester?.toString() || '',
      group: userPreferences?.group || '',
    }), [userPreferences]),
    mode: 'edit',
    onSubmit: async (data: StudentFormValues) => {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      let semesterValue: number | null = null;
      if (data.semester) {
        const semesterStr = data.semester.toString();
        const romanToNumber: Record<string, number> = {
          'I': 1, 'II': 2, 'III': 3, 'IV': 4, 'V': 5, 
          'VI': 6, 'VII': 7, 'VIII': 8, 'IX': 9, 'X': 10
        };
        semesterValue = romanToNumber[semesterStr] || parseInt(semesterStr);
      }
      
      const updatedPreferences = {
        ...userPreferences,
        name: data.name?.trim() || '',
        department: data.department || '',
        program: data.program,
        semester: ((semesterValue && !isNaN(semesterValue)) ? semesterValue : userPreferences?.semester || 1).toString(),
        group: data.group,
        year: data.year,
      };
      
      setUserPreferences(updatedPreferences);
      setTimeout(() => onClose(), 1500);
    },
  });

  React.useEffect(() => {
    if (!isOpen) {
      profileInitialized.current = false;
      return;
    }
    if (!isDataLoading && userPreferences && !profileInitialized.current) {
      profileInitialized.current = true;
      const semesterValue = normalizeSemester(userPreferences.semester);
      const yearValue = findMatchingBatch(availableYears, userPreferences.year) ?? userPreferences.year?.toString() ?? '';
      
      const formValues = {
        name: userPreferences.name || '',
        department: getDepartmentForProgram(userPreferences.program) || userPreferences.department || '',
        program: userPreferences.program || '',
        year: yearValue,
        semester: semesterValue,
        group: normalizeGroup(userPreferences.group),
      };
      
      form.reset(formValues);
      if (setSelectedDepartment) setSelectedDepartment(formValues.department);
      if (setSelectedProgram) setSelectedProgram(formValues.program);
      if (setSelectedYear) setSelectedYear(yearValue);
      if (setSelectedSemester) setSelectedSemester(semesterValue);
    }
  }, [isOpen, isDataLoading, availableYears, userPreferences, form, getDepartmentForProgram, setSelectedDepartment, setSelectedProgram, setSelectedYear, setSelectedSemester]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <style>{`
        .epm-input{width:100%;padding:11px 14px;border-radius:9px;background:rgba(255,255,255,.05);border:1px solid ${T.border};color:#fff;font-family:${T.sans};font-size:13px;outline:none;transition:border-color .15s}
        .epm-input:focus{border-color:rgba(20,184,166,.5)}
        .epm-input.err{border-color:rgba(239,68,68,.5)}
        .epm-input.dis{opacity:.5;cursor:not-allowed}
        .epm-input::placeholder{color:rgba(255,255,255,.3)}
        .epm-input option{background:#1a1a1a;color:#fff}
        .epm-label{display:flex;align-items:center;gap:6px;font-family:${T.mono};font-size:9px;letter-spacing:.1em;color:rgba(255,255,255,.35);margin-bottom:7px;text-transform:uppercase}
        .epm-err{display:flex;align-items:center;gap:4px;color:rgba(239,68,68,.8);font-size:11px;margin-top:6px}
        .epm-btn{flex:1;display:flex;align-items:center;justify-content:center;gap:8px;padding:13px;border-radius:10px;font-family:${T.sans};font-size:13px;font-weight:500;cursor:pointer;transition:.15s;background:${T.surface};border:1px solid ${T.border};color:#fff}
        .epm-btn:hover:not(:disabled){background:rgba(255,255,255,.055)}
        .epm-btn:disabled{opacity:.5;cursor:not-allowed}
        .epm-x:hover{background:rgba(255,255,255,.08)!important;color:#fff!important}
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:w-[95vw] sm:max-w-xl max-h-[92vh] sm:max-h-[86vh] rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
        style={{ background: T.panel, border: `1px solid ${T.border}`, boxShadow: '0 24px 70px rgba(0,0,0,.6)', fontFamily: T.sans, color: T.text, paddingBottom: 'env(safe-area-inset-bottom,0px)' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: T.tealBg, border: `1px solid ${T.tealBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.teal }}>
              <User className="w-4 h-4" />
            </div>
            <h2 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, letterSpacing: '-.01em' }}>Edit Profile</h2>
          </div>
          <button type="button" onClick={onClose} className="epm-x" style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.05)', border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.text40, cursor: 'pointer', transition: '.15s' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Status */}
        {submitStatus !== 'none' && (
          <div style={{ padding: '12px 20px 0' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, fontSize: 12,
              background: submitStatus === 'success' ? T.tealBg : submitStatus === 'error' ? 'rgba(239,68,68,.1)' : T.surface,
              border: `1px solid ${submitStatus === 'success' ? T.tealBorder : submitStatus === 'error' ? 'rgba(239,68,68,.25)' : T.border}`,
              color: submitStatus === 'success' ? T.teal : submitStatus === 'error' ? '#EF4444' : T.text60,
            }}>
              {submitStatus === 'success' && <Check className="w-4 h-4" />}
              {submitStatus === 'error' && <AlertTriangle className="w-4 h-4" />}
              {(submitStatus === 'validating' || submitStatus === 'submitting') && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitStatus === 'success' ? 'Profile updated!' : submitStatus === 'error' ? 'Please fix errors' : submitStatus === 'validating' ? 'Validating…' : 'Updating…'}
            </div>
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Personal Info */}
          <Section icon={User} title="Personal Information">
            <div>
              <label className="epm-label"><User className="w-3 h-3" /> Full Name *</label>
              <input
                type="text"
                value={form.watch('name')}
                onChange={(e) => handleFieldChange('name', e.target.value, form.setValue)}
                className={`epm-input${validationErrors.name ? ' err' : ''}`}
                placeholder="Enter your full name"
              />
              {validationErrors.name && <p className="epm-err"><AlertTriangle className="w-3 h-3" />{validationErrors.name}</p>}
            </div>
          </Section>

          {/* Academic Info */}
          <Section icon={BookOpen} title="Academic Information">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Department */}
              <div>
                <label className="epm-label"><Building className="w-3 h-3" /> Department *</label>
                <select
                  value={form.watch('department')}
                  onChange={(e) => { setSelectedDepartment(e.target.value); form.setValue('department', e.target.value); resetDependentFields('department'); }}
                  disabled={isDataLoading}
                  className={`epm-input${validationErrors.department ? ' err' : ''}${isDataLoading ? ' dis' : ''}`}
                >
                  <option value="">{isDataLoading ? 'Loading…' : 'Select Department'}</option>
                  {departments.map(dept => <option key={dept.id} value={dept.name}>{dept.name}</option>)}
                </select>
                {validationErrors.department && <p className="epm-err"><AlertTriangle className="w-3 h-3" />{validationErrors.department}</p>}
              </div>

              {/* Program */}
              <div>
                <label className="epm-label"><BookOpen className="w-3 h-3" /> Program *</label>
                <select
                  value={form.watch('program')}
                  onChange={(e) => { setSelectedProgram(e.target.value); form.setValue('program', e.target.value); resetDependentFields('program'); }}
                  disabled={!selectedDepartment || isDataLoading}
                  className={`epm-input${validationErrors.program ? ' err' : ''}${(!selectedDepartment || isDataLoading) ? ' dis' : ''}`}
                >
                  <option value="">{isDataLoading ? 'Loading…' : !selectedDepartment ? 'Select department first' : availablePrograms.length === 0 ? 'No programs' : 'Select program'}</option>
                  {availablePrograms.map(program => <option key={program} value={program}>{program}</option>)}
                </select>
                {validationErrors.program && <p className="epm-err"><AlertTriangle className="w-3 h-3" />{validationErrors.program}</p>}
              </div>

              {/* Semester & Year */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="epm-label"><Hash className="w-3 h-3" /> Semester *</label>
                  <select
                    value={form.watch('semester')}
                    onChange={(e) => { setSelectedSemester(e.target.value); form.setValue('semester', e.target.value); resetDependentFields('semester'); }}
                    disabled={!selectedProgram || isDataLoading}
                    className={`epm-input${validationErrors.semester ? ' err' : ''}${(!selectedProgram || isDataLoading) ? ' dis' : ''}`}
                  >
                    <option value="">{!selectedProgram ? 'Select program' : 'Select'}</option>
                    {availableSemesters.map(sem => <option key={sem} value={sem}>Semester {sem}</option>)}
                  </select>
                  {validationErrors.semester && <p className="epm-err"><AlertTriangle className="w-3 h-3" />{validationErrors.semester}</p>}
                </div>
                <div>
                  <label className="epm-label"><Calendar className="w-3 h-3" /> Year *</label>
                  <select
                    value={form.watch('year')}
                    onChange={(e) => { setSelectedYear(e.target.value); form.setValue('year', e.target.value); resetDependentFields('year'); }}
                    disabled={!selectedProgram || isDataLoading}
                    className={`epm-input${validationErrors.year ? ' err' : ''}${(!selectedProgram || isDataLoading) ? ' dis' : ''}`}
                  >
                    <option value="">{!selectedProgram ? 'Select program' : 'Select'}</option>
                    {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                  </select>
                  {validationErrors.year && <p className="epm-err"><AlertTriangle className="w-3 h-3" />{validationErrors.year}</p>}
                </div>
              </div>

              {/* Group */}
              <div>
                <label className="epm-label"><Hash className="w-3 h-3" /> Group *</label>
                <select
                  value={form.watch('group')}
                  onChange={(e) => { form.setValue('group', e.target.value); clearFieldError('group'); }}
                  disabled={!selectedProgram || !selectedSemester || isDataLoading}
                  className={`epm-input${validationErrors.group ? ' err' : ''}${(!selectedProgram || !selectedSemester || isDataLoading) ? ' dis' : ''}`}
                >
                  <option value="">{!selectedProgram || !selectedSemester ? 'Select program & semester' : availableGroups.length === 0 ? 'No groups' : 'Select group'}</option>
                  {availableGroups.map(group => <option key={group} value={group}>Group {group}</option>)}
                </select>
                {validationErrors.group && <p className="epm-err"><AlertTriangle className="w-3 h-3" />{validationErrors.group}</p>}
              </div>
            </div>
          </Section>

          {/* Preview */}
          {(form.watch('name') || form.watch('program')) && (
            <div style={{ background: T.tealBg, border: `1px solid ${T.tealBorder}`, borderRadius: 14, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <User className="w-4 h-4" color={T.teal} />
                <h4 style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', color: T.teal }}>PREVIEW</h4>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                {[
                  ['Name', form.watch('name')], ['Department', form.watch('department')], ['Program', form.watch('program')],
                  ['Year', form.watch('year')], ['Semester', form.watch('semester')], ['Group', form.watch('group')],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k as string}>
                    <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', color: T.text25 }}>{String(k).toUpperCase()}</div>
                    <div style={{ fontSize: 13, color: T.text, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v as string}</div>
                  </div>
                ))}
              </div>
              {form.watch('name') && form.watch('department') && form.watch('program') && form.watch('year') && form.watch('semester') && form.watch('group') && (
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.teal }}>
                  <Check className="w-3 h-3" /> Ready to save
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: 16, borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10, flexShrink: 0 }}>
          <button type="button" onClick={onClose} disabled={submitStatus === 'submitting' || submitStatus === 'validating'} className="epm-btn">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitStatus === 'submitting' || submitStatus === 'validating' || isDataLoading || Object.keys(validationErrors).length > 0}
            className="epm-btn"
            style={{ color: T.teal, borderColor: T.tealBorder, background: T.tealBg }}
          >
            {submitStatus === 'submitting' && <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>}
            {submitStatus === 'validating' && <><Loader2 className="w-4 h-4 animate-spin" /> Validating…</>}
            {submitStatus === 'success' && <><Check className="w-4 h-4" /> Saved!</>}
            {(submitStatus === 'none' || submitStatus === 'error') && <><Save className="w-4 h-4" /> Save</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Section card ──────────────────────────────────────────────────────────────
function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <section style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Icon className="w-4 h-4" color={T.teal} />
        <h3 style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{title}</h3>
      </div>
      {children}
    </section>
  );
}
